// BeginCheckout.jsx
import React, { useEffect, useState, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { Elements, useStripe, useElements } from "@stripe/react-stripe-js"
import AldooStoreAPI from "aldoo-ra/Store/api"
import { api } from "aldoo-ra/API"
import Value from "aldoo-ra/Value"
import { WalletIcon } from "@heroicons/react/24/outline"
import Icon from "aldoo-ra/Icon"
import PurchaseFlowController from "aldoo-ra/Store/PurchaseFlowController"
import { getStripePromise } from "aldoo-ra/Store/stripe-client"
import { selectionOf } from "aldoo-ra/Store/lines"

// Publishable key comes from the server (ParamIO) via store_config —
// Stripe.js still downloads lazily on first render of this element
const stripePromise = () => getStripePromise()

// Steps rendered by this element itself; every other step of a flow is a
// project step rendered through its `component` (element or fragment id)
const BUILTIN_STEPS = new Set(['cart', 'email', 'invoice', 'payment', 'payment-reuse', 'success'])

const DEFAULT_ELEMENTS_APPEARANCE = {
  variables: {
    colorDanger: "#df1b41",
    colorBackground: "#f9f9f9",
    colorText: "#090909",
  },
}

// Resolve a step label / title that may be a function of the flow context
const resolveText = (value, context) => (typeof value === 'function' ? value(context) : value)

function CheckoutContent({ store, controller }) {
  const texts = TextBundle({ id: "aldoo-store" })
  const [paymentInfo] = Value("aldoo-payment-info")
  const [preloadedInvoiceData, setPreloadedInvoiceData] = Value("aldoo-preloaded-invoice-data")
  const [paymentInitError, setPaymentInitError] = Value("aldoo-payment-init-error")
  const [showingNewPayment, setShowingNewPayment] = useState(false)
  const [showingInvoice, setShowingInvoice] = useState(false)
  // Initialize with preloaded data if available
  const [invoiceData, setInvoiceData] = useState(() => preloadedInvoiceData || null)
  const [invoiceStepWasShown, setInvoiceStepWasShown] = useState(false)
  // One payment initialization at a time. A refresh requested while one is
  // in flight runs right after it (never concurrently: two parallel
  // initializations would create two payments / reservations server-side).
  const paymentRequestRef = useRef({ inFlight: false, token: 0, pendingRefresh: false })

  // Use controller state instead of local state
  const currentStep = controller.currentStep
  const { canContinue, loading, continueText, hideButtons, handleContinue, handleBack, getFlowSteps, isStepActive, currentStepDef } = controller

  const {
    cart,
    setPaymentInfo,
    checkoutVisible,
    emailDialogVisible,
    hideCheckout,
    getElement,
    getFragment,
    cartChanged,
    purchaseFlow,
  } = store

  const config = store.config || {}
  const modalConfig = config.modals?.checkout || {}
  const paymentConfig = config.payment || {}
  const flowDefinition = purchaseFlow?.flowDefinition || {}

  // Get components
  const CheckoutForm = getElement("checkout-form")
  const ReusePaymentDialog = getElement("reuse-payment-dialog")
  const InvoiceStep = getElement("invoice-step")
  const EmailInputDialog = getElement("email-input-dialog")
  const CheckoutWithInvoice = getElement("checkout-with-invoice")
  const CheckoutPreparingPayment = getFragment("checkout-preparing-payment")
  const CheckoutPaymentInitError = getFragment("checkout-payment-init-error")
  const CheckoutContainer = getFragment("checkout-container")
  // Optional project slot rendered inside the checkout modal (toasts, banners…)
  const CheckoutExtras = getFragment("checkout-extras")

  const elementsOptions = (clientSecret) => ({
    clientSecret,
    loader: "always",
    appearance: DEFAULT_ELEMENTS_APPEARANCE,
    ...(resolveText(flowDefinition.elementsOptions, { store }) || {}),
  })

  // Initialize invoice data from preloaded data before flow starts
  useEffect(() => {
    if (checkoutVisible && preloadedInvoiceData && !invoiceData) {
      setInvoiceData({
        ...preloadedInvoiceData,
        useSavedBillingDetails: true
      })
      setPreloadedInvoiceData(null)
    }
  }, [checkoutVisible, preloadedInvoiceData])

  // Flow initialization is now handled by PurchaseFlowController

  // Reset showingNewPayment when returning to payment-reuse step
  useEffect(() => {
    if (currentStep === 'payment-reuse' && showingNewPayment) {
      setShowingNewPayment(false)
    }
  }, [currentStep])

  // Remove focus from any focused button when step changes
  useEffect(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }, [currentStep])

  /**
   * Create the payment for the cart. A flow may replace the default
   * single-product `initiate_payment` with its own initializer, e.g. a
   * multi-line, amount-based checkout:
   *   initializePayment: async ({ store, cart, stepData, getData, auth }) => paymentInfo
   * It resolves to the paymentInfo object ({ requiresPayment, clientSecret, ... })
   * or throws / returns { error } to surface a payment initialization error,
   * or { skipped: true } when it cannot be made yet (the address is still to
   * come): nothing is shown, a later step that refreshes the payment makes it.
   */
  const initializePaymentInfo = async () => {
    const cartItem = cart[0]
    if (typeof flowDefinition.initializePayment === 'function') {
      return flowDefinition.initializePayment({
        store,
        cart,
        stepData: controller.stepData,
        getData: controller.getData,
        context: controller.getFlowContext(),
        auth: store.auth || store.tempAuthForPurchase,
        promoCode: store.promoCode,
      })
    }

    // Use custom auth if available (for existing users not logged in)
    let response
    if (store.customAuth) {
      const storeAPIWithAuth = api("store", store.customAuth)
      response = await storeAPIWithAuth({
        initiate_payment: {
          platform: "Web",
          pid: cartItem.pid,
          // the buyer's selection (sku, choices, components), when the line has one
          ...selectionOf(cartItem),
          promo_code: store.promoCode,
        }
      })
    } else {
      response = await AldooStoreAPI.initializePayment({
        pid: cartItem.pid,
        ...selectionOf(cartItem),
        promo_code: store.promoCode,
      })
    }
    if (!response?.error) {
      response.pid = cartItem.pid
      response.promoCode = store.promoCode
    }
    return response
  }

  const validateAndRefreshPaymentInfo = async ({ force = false } = {}) => {
    if (!cart || cart.length === 0) return
    // the flow still loading (a checkout reopened by a page refresh): its own initializer is not
    // known yet, and the default single-product payment is not this cart's — asked once it loads
    if (!purchaseFlow) return
    if (paymentRequestRef.current.inFlight) {
      if (force) paymentRequestRef.current.pendingRefresh = true
      return
    }

    // Check if we have cached payment info but need to validate the saved payment method
    if (!force && paymentInfo && !cartChanged && paymentInfo.savedPaymentMethod) {
      // Check if the saved payment method is expired or stale
      const savedMethod = paymentInfo.savedPaymentMethod
      const currentYear = new Date().getFullYear()
      const currentMonth = new Date().getMonth() + 1

      // If card is expired or close to expiry, force refresh
      const isExpiredOrExpiring = savedMethod.type === 'card' &&
        (savedMethod.expiryYear < currentYear ||
         (savedMethod.expiryYear === currentYear && savedMethod.expiryMonth <= currentMonth))

      if (isExpiredOrExpiring) {
        console.log("BeginCheckout - Saved payment method is expired/expiring, forcing refresh")
        // Force refresh by not returning early
      } else {
        // Validate with server to ensure we have the latest payment method
        try {
          const currentMethods = await AldooStoreAPI.getPaymentMethods()
          const currentDefaultMethod = currentMethods.find(method => method.isDefault)

          if (!currentDefaultMethod || currentDefaultMethod.id !== savedMethod.id ||
              currentDefaultMethod.expiryYear !== savedMethod.expiryYear ||
              currentDefaultMethod.expiryMonth !== savedMethod.expiryMonth) {
            console.log("BeginCheckout - Saved payment method differs from server, forcing refresh")
            console.log("Cached method:", savedMethod)
            console.log("Server method:", currentDefaultMethod)
          } else {
            setPaymentInfo({ ...paymentInfo })
            return
          }
        } catch (error) {
          console.error("Failed to validate payment methods:", error)
          // Continue with refresh on error
        }
      }
    }

    const token = ++paymentRequestRef.current.token
    paymentRequestRef.current.inFlight = true
    const isLatest = () => token === paymentRequestRef.current.token

    try {
      setPaymentInitError(null)
      const response = await initializePaymentInfo()
      if (!isLatest()) return
      if (response?.skipped) return

      if (!response || response.error) {
        console.error("Failed to initialize payment:", response?.error)
        setPaymentInitError(response || { error: "payment_init_failed" })
        return
      }

      setPaymentInfo(response)
    } catch (error) {
      if (!isLatest()) return
      console.error("Failed to initialize payment:", error)
      setPaymentInitError(error?.error || error?.message ? error : { error: "payment_init_failed" })
    } finally {
      if (isLatest()) {
        paymentRequestRef.current.inFlight = false
        if (paymentRequestRef.current.pendingRefresh) {
          paymentRequestRef.current.pendingRefresh = false
          validateAndRefreshPaymentInfo({ force: true })
        }
      }
    }
  }

  useEffect(() => {
    if (checkoutVisible) {
      // Invoice data loading is now handled in a separate useEffect above

      // Initialize payment info when checkout opens IF user is authenticated
      // This ensures billing details are available for flow initialization
      // For non-authenticated users, payment info will load after email step
      const hasAuth = store.auth || store.tempAuthForPurchase
      if (!paymentInfo && hasAuth) {
        validateAndRefreshPaymentInfo()
      }

      // Reset invoice step tracking when opening a new checkout session
      setInvoiceStepWasShown(false)
    } else {
      // Reset invoice data when checkout is closed
      setInvoiceData(null)
      setShowingInvoice(false)
      setShowingNewPayment(false)
      setPaymentInitError(null)
    }
  }, [checkoutVisible, invoiceData, preloadedInvoiceData, store.auth, store.tempAuthForPurchase, purchaseFlow])

  // A persisted paymentInfo (previous session, or one whose amount no longer
  // matches the cart) is dropped when the checkout opens, so the flow always
  // starts from a fresh payment. Opt-in: config.payment.reinitializeOnOpen.
  useEffect(() => {
    if (!checkoutVisible || !paymentConfig.reinitializeOnOpen) return
    if (!paymentInfo) return
    setPaymentInfo(null)
  }, [checkoutVisible])

  // Steps flagged refreshPayment re-create the payment when entered — the
  // amount may depend on data collected by earlier steps (address, shipping…)
  useEffect(() => {
    if (!checkoutVisible || !currentStepDef?.refreshPayment) return
    const hasAuth = store.auth || store.tempAuthForPurchase
    if (!hasAuth) return
    validateAndRefreshPaymentInfo({ force: true })
  }, [currentStep, checkoutVisible, purchaseFlow])

  // Listen for payment methods updates and refresh payment info
  useEffect(() => {
    const handlePaymentMethodsUpdated = () => {
      console.log("BeginCheckout - payment-methods-updated event received, refreshing payment info")
      // Force re-initialization of payment info to get latest saved payment method
      if (checkoutVisible && cart && cart.length > 0) {
        validateAndRefreshPaymentInfo({ force: true })
      }
    }

    window.addEventListener("payment-methods-updated", handlePaymentMethodsUpdated)
    return () => {
      window.removeEventListener("payment-methods-updated", handlePaymentMethodsUpdated)
    }
  }, [checkoutVisible, cart, store.promoCode])

  if (paymentInfo && !paymentInfo.requiresPayment) return null
  if (!checkoutVisible) return null

  const shouldShowReuseDialog =
    currentStep === 'payment-reuse' &&
    paymentInfo?.savedPaymentMethod &&
    !showingNewPayment &&
    !showingInvoice

  // Show invoice step if:
  // - We don't have invoiceData yet (user hasn't filled it)
  // - Always show first to give user option to request invoice
  const shouldShowInvoiceFirst = !showingInvoice && !invoiceData

  // All step navigation is now handled by PurchaseFlowController via PubSub

  const handleShowInvoice = () => {
    setShowingInvoice(true)
    // Mark that invoice step was shown since user manually opened it
    setInvoiceStepWasShown(true)
  }

  const handlePaymentComplete = async () => {
    // Complete payment with stored invoice data
    if (store.completePaymentWithInvoice) {
      await store.completePaymentWithInvoice(invoiceData)
    } else {
      store.onPaymentSuccess()
    }
  }

  // Store invoice data in store for access by payment submission
  if (invoiceData && !store.invoiceData) {
    store.invoiceData = invoiceData
  }

  const flowContext = controller.getFlowContext()

  // Get all flow steps and current index
  // Use getFlowSteps from controller
  const flowSteps = getFlowSteps().map((step) => ({ ...step, name: resolveText(step.name, flowContext) }))

  const getCurrentStepIndex = () => {
    if (!purchaseFlow) return -1
    // If current step is payment-reuse, treat it as payment for display purposes
    const displayStep = currentStep === 'payment-reuse' ? 'payment' : currentStep
    return flowSteps.findIndex(s => s.id === displayStep)
  }

  // Render flow progress header
  const renderFlowProgress = (interactive = false) => {
    const steps = flowSteps
    const currentIndex = getCurrentStepIndex()

    if (steps.length === 0) return null

    return (
      <>
        {/* Mobile: the step the buyer is on, as the dialog's title — bold, in
            the text colour, with where it is in the flow. (A strip sliding one
            screen-wide title per step showed the wrong step whenever the
            dialog was narrower than the screen.) */}
        <div className="block md:hidden -mx-6 -mt-6 mb-4 bg-white shadow-md" data-testid="checkout-step-title">
          <div className="h-[52px] flex items-center justify-center gap-2 px-14">
            <span className="text-[18px] font-bold text-text truncate">{steps[currentIndex]?.name}</span>
            {steps.length > 1 && currentIndex >= 0 && (
              <span className="shrink-0 text-[13px] font-semibold text-text_secondary">
                {currentIndex + 1}/{steps.length}
              </span>
            )}
          </div>
        </div>

        {/* Desktop version - original horizontal menu */}
        <div className="hidden md:flex items-center mb-4 py-4 pr-10">
          {steps.map((step, index) => {
            const isActive = index === currentIndex
            const isPast = index < currentIndex
            const isLast = index === steps.length - 1

            return (
              <React.Fragment key={step.id}>
                <div className="flex-1 flex items-center justify-center">
                  {/* every name on one line: the tick sits before it without moving it, and every name
                      keeps the underline's room (transparent when it is not the step the buyer is on) */}
                  <div
                    className={`
                      relative text-sm md:text-base font-medium text-center whitespace-nowrap leading-none transition-all pt-1 pb-2 border-b-4
                      ${isActive ? 'text-primary border-primary font-semibold' : 'text-text border-transparent'}
                    `}
                  >
                    {isPast && (
                      <svg className="absolute -left-6 top-1/2 -translate-y-[60%] w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {step.name}
                  </div>
                </div>
                {!isLast && (
                  <div className="w-px h-6 bg-bordered flex-shrink-0"></div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </>
    )
  }

  // Determine modal title based on current step
  const getModalTitle = () => {
    const custom = resolveText(currentStepDef?.title, flowContext)
    if (custom) return custom
    if (currentStep === 'email') return texts?.enterYourEmail || 'Enter Your Email'
    if (currentStep === 'invoice') return texts?.invoiceDetails || 'Invoice details'
    return texts?.payment || 'Payment'
  }

  // Determine if we're on a payment step
  const isPaymentStep = currentStep === 'payment' || currentStep === 'payment-reuse' || !!currentStepDef?.isPayment

  // Render unified footer with Back/Continue buttons
  const renderFooter = () => {
    const buttonText = continueText

    // Show wallet icon only when button text indicates a payment action (not "Continue")
    // This ensures icon shows for "Pay Now" but not when selecting "add new payment method"
    const isPaymentAction = buttonText && (
      buttonText.toLowerCase().includes('pay') ||
      buttonText.toLowerCase().includes('submit')
    )
    const showWalletIcon = isPaymentStep && canContinue && !loading && isPaymentAction

    // Hide buttons when magic link verification is showing
    if (hideButtons) {
      return null
    }

    return (
      <div className="flex gap-3 mt-4">
        <Button
          variant="secondary_s"
          data-testid="flow-back"
          onClick={(e) => {
            e.currentTarget.blur() // Remove focus after click
            handleBack()
          }}
          disabled={loading}
          className="flex-1 !h-auto p-4"
        >
          {texts?.back || "Back"}
        </Button>

        <Button
          variant="primary_s"
          data-testid={isPaymentStep ? "pay-button" : "flow-continue"}
          onClick={(e) => {
            e.currentTarget.blur() // Remove focus after click
            handleContinue()
          }}
          disabled={!canContinue || loading}
          className={`flex-1 !h-auto p-4 gap-2 relative ${!canContinue || loading ? 'opacity-50' : ''}`}
        >
          {loading && (
            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {showWalletIcon ? (
            <WalletIcon
              className="h-5 w-5 text-white animate-slideInFromLeft"
              key="wallet-in"
            />
          ) : (
            isPaymentStep && !canContinue && (
              <WalletIcon
                className="h-5 w-5 text-white animate-slideOutToRight"
                key="wallet-out"
              />
            )
          )}
          {loading ? (currentStep === 'email' ? (texts?.verifying || "Verifying...") : (texts?.loading || "Loading...")) : buttonText}
        </Button>
      </div>
    )
  }

  // Project steps: rendered through their `component` id (element first,
  // then fragment). Mounted only while active unless `keepMounted` is set.
  const renderCustomSteps = () => {
    const steps = (purchaseFlow?.getAllSteps?.() || []).filter((step) => !BUILTIN_STEPS.has(step.id))
    return steps.map((step) => {
      const Component = getElement(step.component) || getFragment(step.component)
      if (!Component) return null
      const isActive = currentStep === step.id
      if (!isActive && !step.keepMounted) return null
      const content = (
        <Component
          store={store}
          controller={controller}
          step={step}
          isActive={isActive}
          stepData={controller.getData(step.id)}
        />
      )
      return (
        <div key={step.id} className={isActive ? 'h-full' : 'hidden'}>
          {step.stripe && paymentInfo?.clientSecret ? (
            <Elements stripe={stripePromise()} options={elementsOptions(paymentInfo.clientSecret)}>
              {content}
            </Elements>
          ) : content}
        </div>
      )
    })
  }

  // Built-in steps are mounted only when the flow contains them (a project
  // flow may leave the invoice step out, so no hidden AddressElement loads)
  const flowHas = (stepId) => !purchaseFlow || purchaseFlow.getStepIndex(stepId) !== -1

  // Render all steps but show/hide based on currentStep to preserve Stripe form state
  const renderAllSteps = () => {
    return (
      <>
        {/* Email step - doesn't need payment info */}
        {flowHas('email') && (
        <div className={currentStep === 'email' ? '' : 'hidden'}>
          <EmailInputDialog
            store={store}
            initialEmail={store.auth?.email || store.tempAuthForPurchase?.email || store.purchaseUserEmail || ""}
            isActive={isStepActive('email')}
          />
        </div>
        )}

        {/* Invoice step - wrapped in Elements for Stripe AddressElement */}
        {flowHas('invoice') && (
        <div className={currentStep === 'invoice' ? 'h-full' : 'hidden'}>
          {paymentInfo && paymentInfo.clientSecret ? (
            <Elements
              key={paymentInfo.clientSecret}
              stripe={stripePromise()}
              options={elementsOptions(paymentInfo.clientSecret)}
          >
            <CheckoutWithInvoice store={store} invoiceData={invoiceData}>
              <InvoiceStep
                store={store}
                isOpen={currentStep === 'invoice'}
              />
            </CheckoutWithInvoice>
          </Elements>
          ) : (
            <CheckoutPreparingPayment />
          )}
        </div>
        )}

        {/* Payment step - wrapped in Elements to preserve PaymentElement state.
            Keyed by the client secret: Stripe Elements cannot switch secret once
            created, so a refreshed payment remounts the form */}
        {(flowHas('payment') || flowHas('payment-reuse')) && (
        <div className={(currentStep === 'payment' || currentStep === 'payment-reuse') ? 'h-full' : 'hidden'}>
          {paymentInfo && paymentInfo.clientSecret ? (
            <Elements
              key={paymentInfo.clientSecret}
              stripe={stripePromise()}
              options={elementsOptions(paymentInfo.clientSecret)}
          >
            <CheckoutWithInvoice store={store} invoiceData={invoiceData}>
              {shouldShowReuseDialog ? (
                <div className="h-full flex flex-col justify-between">
                  <ReusePaymentDialog
                    store={store}
                    onSelectNewPayment={() => setShowingNewPayment(true)}
                    isActive={isStepActive('payment-reuse')}
                  />
                </div>
              ) : (
                <CheckoutForm
                  store={store}
                  isActive={isStepActive('payment')}
                />
              )}
            </CheckoutWithInvoice>
          </Elements>
          ) : (
            <CheckoutPreparingPayment />
          )}
        </div>
        )}

        {renderCustomSteps()}
      </>
    )
  }

  // Check if we're still loading initial data. Built-in steps other than the
  // email wait for the payment; a step definition may say otherwise
  // (requiresPaymentInfo: false for project steps collecting data first).
  const stepRequiresPaymentInfo = currentStepDef?.requiresPaymentInfo ?? (currentStep !== 'email')
  const isLoadingInitialData = checkoutVisible && stepRequiresPaymentInfo && !paymentInfo
  const isCenteredStep = currentStep === 'email' || currentStep === 'invoice' || currentStepDef?.layout === 'centered'

  const handleClose = () => {
    const confirm = modalConfig.confirmClose
    if (!confirm || typeof window === 'undefined') return hideCheckout()
    import("aldoo-ra/PromptDialog").then(({ showPromptDialog }) => {
      showPromptDialog({
        title: resolveText(confirm.title, flowContext) || texts?.closeCheckoutTitle || "Close Checkout?",
        message: resolveText(confirm.message, flowContext) || texts?.closeCheckoutMessage || "Are you sure you want to close? Your progress will be lost.",
        confirmText: resolveText(confirm.confirmText, flowContext) || texts?.closeCheckoutConfirm || "Close",
        cancelText: resolveText(confirm.cancelText, flowContext) || texts?.cancel || "Cancel",
        onConfirm: hideCheckout,
      })
    })
  }

  // Single unified modal that shows different steps
  return (
    <Modal
      // opened once the flow knows its first step: never a flash of another step first
      // open on the flow's first step, never before: a reload into a checkout (?checkout=1) begins it while
      // the flows still load, and a dialog without one would paint the default step ("email") for a moment
      isOpen={(checkoutVisible && controller.flowInitialized) || emailDialogVisible}
      variant={modalConfig.variant || "store_checkout"}
      className={modalConfig.className ?? "md:w-[750px] md:!max-h-[85vh]"}
      onClose={handleClose}
      allowCloseBtn={!!modalConfig.allowClose}
      closeOnEscape={!!modalConfig.allowClose}
      styleOverrides={modalConfig.styleOverrides}
    >
      {/* the body's size: by default as tall as its step (up to 85vh); a project giving the dialog a
          fixed height (modals.checkout.bodyClassName) has the body fill it, the buttons at its foot */}
      <div
        className={modalConfig.bodyClassName || "flex flex-col h-full md:h-auto md:max-h-[calc(85vh-2rem)]"}
        data-testid="checkout-dialog"
        data-step={currentStep}
      >
        <div className="flex-shrink-0">
          {renderFlowProgress(false)}
        </div>
        <div className="flex-1 bg-paper rounded-2xl border border-gray-100 min-h-0 relative overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper">
          {paymentInitError && stepRequiresPaymentInfo && !paymentInfo ? (
            <CheckoutPaymentInitError
              store={store}
              error={paymentInitError}
              onRetry={() => validateAndRefreshPaymentInfo({ force: true })}
            />
          ) : isLoadingInitialData ? (
            <CheckoutPreparingPayment />
          ) : isCenteredStep ? (
            <div className="min-h-full flex items-center justify-center py-4">
              <CheckoutContainer store={store}>
                {renderAllSteps()}
              </CheckoutContainer>
            </div>
          ) : (
            <CheckoutContainer store={store}>
              {renderAllSteps()}
            </CheckoutContainer>
          )}
        </div>
        <div className="flex-shrink-0 pb-safe">
          {renderFooter()}
        </div>
        {/* refreshPayment: the payment made again (a reservation that ran out, taken anew) */}
          {CheckoutExtras && <CheckoutExtras store={store} controller={controller} refreshPayment={() => validateAndRefreshPaymentInfo({ force: true })} />}
      </div>
    </Modal>
  )
}

// Main component that wraps CheckoutContent with PurchaseFlowController
export default function BeginCheckout({ store }) {
  return (
    <PurchaseFlowController store={store}>
      {(controller) => <CheckoutContent store={store} controller={controller} />}
    </PurchaseFlowController>
  )
}
